/**
 * Tab Retour (keuze 16), zelfde opbouw als Bestelling: de laatste retour
 * groot met de tijdlijn (sessie 163: zeven stappen uit de eigen tabel,
 * Aangevraagd, Label gemaakt, Bij pakketpunt, Onderweg, Binnen in het
 * magazijn, Verwerkt, Gecrediteerd; plus Geannuleerd of Afgewezen als de
 * retour zo eindigde); het pakket met track en trace; waarde, verzendkosten (klant of
 * VoerOnline) en afloop. Daaronder eerdere retouren compact (klik opent),
 * de tellers (retouren, bestellingen, percentage) en coulance.
 * Sessie 141-E: het retournummer linkt naar het retourdossier
 * (`/retouren?retour=<id>`), het ordernummer naar Lightspeed zoals in de tab
 * Bestelling; zonder id of link blijft het platte tekst.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Coulance } from "../../Klanten/kaart";
import type { KlantKaart } from "../../Klanten/types";
import { euro, veiligeLink } from "../opmaak";
import { kaart10Api, kaart10Sleutels, type KaartRetourTab } from "./api";
import { dagDatum } from "./hulp";
import { PakketRegel, Stappen } from "./Tijdlijn10";

function Retourblok({ retour, laatste }: { retour: KaartRetourTab; laatste: boolean }) {
  const orderLink = veiligeLink(retour.order_lightspeed_url);
  return (
    <div className="kk10-blok" data-testid="retourblok">
      <div className="bh">
        {laatste ? "Laatste retour" : "Retour"}{" "}
        {retour.retour_id ? (
          <Link
            className="num"
            to={`/retouren?retour=${encodeURIComponent(retour.retour_id)}`}
            title="Naar het retourdossier"
          >
            {retour.retournummer}
          </Link>
        ) : (
          <span className="num">{retour.retournummer}</span>
        )}
        {retour.ordernummer ? (
          <span className="stil">
            {" "}
            bij order{" "}
            {orderLink ? (
              <a
                className="num"
                href={orderLink}
                target="_blank"
                rel="noopener noreferrer"
                title="Order openen in Lightspeed (nieuw tabblad)"
              >
                {retour.ordernummer}
              </a>
            ) : (
              <span className="num">{retour.ordernummer}</span>
            )}
          </span>
        ) : null}
        <span className="r">{retour.reden ?? "reden onbekend"}</span>
      </div>
      <Stappen stappen={retour.stappen} label={`Verloop van retour ${retour.retournummer}`} />
      {retour.pakket ? (
        <PakketRegel pakket={retour.pakket} titel="Pakket" />
      ) : (
        <div className="kk10-pakket">
          <span className="n">Pakket</span>
          <span className="kk10-onbekend">geen retourpakket bekend</span>
        </div>
      )}
      <div className="bb">
        <span className="l">Waarde</span>
        <span className="num">{euro(retour.waarde_cents)}</span>
        {retour.producten.length > 0 ? (
          <>
            <span className="l">Retour</span>
            <span>{retour.producten.join(", ")}</span>
          </>
        ) : null}
        <span className="l">Kosten</span>
        <span data-kosten={retour.kosten_voor}>{retour.kosten_tekst}</span>
        <span className="l">Afloop</span>
        <span>{retour.afloop}</span>
      </div>
    </div>
  );
}

export function TabRetour({ kaart }: { kaart: KlantKaart }) {
  const [gekozen, setGekozen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: kaart10Sleutels.retouren(kaart.id),
    queryFn: () => kaart10Api.retouren(kaart.id),
  });

  if (q.isLoading) return <p className="stil">Retouren worden geladen.</p>;
  if (q.isError || !q.data)
    return (
      <p className="v6-fout" role="alert">
        De retouren konden niet geladen worden.
      </p>
    );
  const { retouren, aantal, aantal_bestellingen } = q.data;
  const getoond = retouren.find((r) => r.retournummer === gekozen) ?? retouren[0] ?? null;
  const eerdere = retouren.filter((r) => r !== getoond);
  const procent =
    aantal_bestellingen > 0 ? `${Math.round((100 * aantal) / aantal_bestellingen)}%` : "onbekend";
  return (
    <>
      {getoond ? (
        <Retourblok retour={getoond} laatste={getoond === retouren[0]} />
      ) : (
        <div className="kk10-blok">
          <div className="bb">
            <span className="vol stil">Geen retouren.</span>
          </div>
        </div>
      )}
      {eerdere.length > 0 ? (
        <div className="kk10-blok">
          <div className="bh">{getoond === retouren[0] ? "Eerdere retouren" : "Andere retouren"}</div>
          <ul className="kk10-lijstje">
            {eerdere.map((r) => (
              <li key={r.retournummer}>
                <button
                  type="button"
                  onClick={() => setGekozen(r.retournummer)}
                  aria-label={`Retour ${r.retournummer} openen`}
                >
                  <span className="d num">{dagDatum(r.aangevraagd_op)}</span>
                  <span className="w">
                    <span className="nr">{r.retournummer}</span>
                    {` · ${r.afloop}`}
                  </span>
                  <span className="num">{euro(r.waarde_cents)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="kk10-tegels" data-testid="retour-tellers">
        <div>
          <b className="num">{aantal}</b>
          <small>retouren in totaal</small>
        </div>
        <div>
          <b className="num">{aantal_bestellingen}</b>
          <small>bestellingen</small>
        </div>
        <div>
          <b className="num">{procent}</b>
          <small>retourpercentage</small>
        </div>
      </div>
      <p className="stil kk10-voet" data-testid="klachten">
        {`Klachten in ${kaart.cijfers.klachten_maanden} maanden: ${kaart.cijfers.klachten}`}
      </p>
      <div className="kk10-blok">
        <div className="bh">Coulance, tegoed en codes</div>
        <div className="kk10-in">
          <Coulance coulance={kaart.coulance} />
        </div>
      </div>
    </>
  );
}
